import { useEffect, useState } from 'react';
import { api, type AreaRollup } from '../../lib/api';

/**
 * AREA1 (2026-10-26) - one area's "who opened" card on the Portals page: "12 of 30 opened (15 counting scanners) ·
 * 3 downloaded", with each lead's page underneath on demand. "Opened" leaves out mail scanners and the first minute
 * after a link was minted (backend portalAreaRollup.ts); the raw count sits beside it so nothing is hidden.
 * A failed read says so - it never draws zeros, which would read as "nobody opened it".
 */

const areaLabel = (area: string): string => {
  const words = area.replace(/-/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};
const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
const when = (iso: string) => new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

export default function AreaRollupCard({ area }: { area: string }) {
  const [rollup, setRollup] = useState<AreaRollup | null>(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let live = true;
    api
      .getAreaRollup(area)
      .then((res) => live && setRollup(res.rollup))
      .catch((e) => live && setError(e instanceof Error ? e.message : 'Could not load who opened this area'));
    return () => {
      live = false;
    };
  }, [area]);

  return (
    <div data-testid={`area-rollup-${area}`} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="px-5 py-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="font-semibold text-gray-900">{areaLabel(area)} - area pages</p>
          {error ? (
            <p className="text-sm text-red-600">{error}</p>
          ) : !rollup ? (
            <p className="text-sm text-gray-500">Loading who opened…</p>
          ) : (
            <p className="text-sm text-gray-700">
              {rollup.opened} of {rollup.portals} opened ({rollup.openedRaw} counting scanners) · {rollup.downloaded} downloaded
            </p>
          )}
        </div>
        {rollup && rollup.portals > 0 && (
          <button
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            className="px-3 py-1.5 border border-gray-300 text-sm rounded-lg hover:bg-gray-50"
          >
            {open ? 'Hide' : 'Who opened'}
          </button>
        )}
      </div>
      {open && rollup && (
        <ul className="border-t border-gray-100 divide-y divide-gray-100">
          {rollup.perPortal.map((p) => (
            <li key={p.id} className="px-5 py-2 flex items-center justify-between gap-3 flex-wrap text-sm">
              <span className="text-gray-900 min-w-0 truncate">{p.title}</span>
              <span className={p.opened ? 'text-success' : 'text-gray-500'}>
                {p.opened
                  ? `Opened · ${plural(p.views, 'view')}${p.downloads ? ` · ${plural(p.downloads, 'download')}` : ''}${p.lastSeen ? ` · last ${when(p.lastSeen)}` : ''}`
                  : `Not opened${p.rawViews ? ` (${plural(p.rawViews, 'scanner hit')})` : ''}`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
